import { Navigate, RouteObject } from "react-router-dom";
import AuthenticationWrapper from "./AuthenticationWrapper";
import { Authorization } from "./components/Authorization";
import { AskQuestion } from "./components/Deepchat/AskQuestion";
import QuerSalesData from "./components/Deepchat/QuerySales/QuerSalesData";
import { SessionThread } from "./components/Deepchat/SessionThread";
import SummarizeDocument from "./components/Deepchat/SummarizeDocument/SummarizeDocument";
import AddStudy from "./components/MarketResearch/AddStudy/AddStudy";
import AllStudies from "./components/MarketResearch/AllStudies/AllStudies";
import GenerateSummary from "./components/MarketResearch/GenerateSummary/GenerateSummary";
import ManageStudies from "./components/MarketResearch/ManageStudy/ManageStudies";
import StudyChat from "./components/MarketResearch/StudyChat/StudyChat";
import StudySummary from "./components/MarketResearch/StudySummary/StudySummary";
import ViewStudy from "./components/MarketResearch/ViewStudy/ViewStudy";
import ViewTranscript from "./components/MarketResearch/ViewTransript/ViewTranscript";
import SalesDataAsk from "./components/SalesData/SalesDataAsk";
import SalesDataSession from "./components/SalesData/SalesDataSession";
import { ErrorPage, HomePage } from "./pages";
import AppLayout from "./pages/AppLayout";
import { CustomDashboard } from "./pages/CustomDashboard";
import CustomDashboardLanding from "./pages/CustomDashboardLanding";
import DeepchatPage from "./pages/DeepchatPage";
import MarketResearchPage from "./pages/MarketResearchPage";
import RetrosPage from "./pages/RetrosPage";
import SalesDataPage from "./pages/SalesDataPage";
import TalkToDataPage from "./pages/TalkToDataPage";

import { RouteId } from "./RouteId";
import ViewRespondents from "./components/MarketResearch/ViewRespondents/ViewRespondents";
import EditStudy from "./components/MarketResearch/ManageStudy/EditStudy/EditStudy";

export const FlattenRoutes = (routes: RouteObject[], flatRoutes: RouteObject[] | undefined = undefined) => {
  flatRoutes = flatRoutes ?? [];

  routes.forEach((route) => {
    if (route.id) {
      flatRoutes.push(route);
    }
    if (route.children) {
      FlattenRoutes(route.children, flatRoutes);
    }
  });

  return flatRoutes;
};

export const FindRouteById = (routes: RouteObject[], id: string) => {
  const routesWithIds = FlattenRoutes(routes);
  const route = routesWithIds.find((_route) => _route.id === id);

  if (!route?.path) {
    console.error({ route });
    throw new Error(`Route ID "${id}" either does not exist or does not have a valid path defined.`);
  }

  return route;
};

export const Routes: RouteObject[] = [
  {
    path: "/",
    element: <AuthenticationWrapper />,
    errorElement: <ErrorPage />,
    id: RouteId.Root,
    children: [
      {
        path: "/auth",
        element: <Authorization />,
      },
      {
        path: "/",
        element: <AppLayout />,
        children: [
          {
            element: <Navigate replace to="/deepchat" />,
          },
          {
            path: "/explorer",
            element: <HomePage />,
            id: RouteId.Explore.Root,
          },
          {
            path: "/market-research",
            element: <MarketResearchPage />,
            id: RouteId.MarketResearch.Root,
            children: [
              {
                path: "/market-research",
                element: <AllStudies />,
                id: RouteId.MarketResearch.Studies,
              },
              {
                path: "/market-research/study/:studyId",
                element: <ViewStudy />,
                id: RouteId.MarketResearch.ViewStudy,
              },
              {
                path: "/market-research/study/:studyId/respondents",
                element: <ViewRespondents />,
                id: RouteId.MarketResearch.ViewRespondents,
              },
              {
                path: "/market-research/threads/:id?",
                element: <StudyChat />,
                id: RouteId.MarketResearch.Chat,
              },
              {
                path: "/market-research/study/:studyId/transcript/:respondentId",
                element: <ViewTranscript />,
                id: RouteId.MarketResearch.ViewTranscript,
              },
              {
                path: "/market-research/study/:studyId/summary",
                element: <StudySummary />,
                id: RouteId.MarketResearch.StudySummary,
              },
              {
                path: "/market-research/study/:studyId/generate-summary",
                element: <GenerateSummary />,
                id: RouteId.MarketResearch.GenerateSummary,
              },
              {
                path: "/market-research/manage-studies",
                element: <ManageStudies />,
                id: RouteId.MarketResearch.ManageStudies,
              },
              {
                path: "/market-research/add-study",
                element: <AddStudy />,
                id: RouteId.MarketResearch.AddStudy,
              },
              {
                path: "/market-research/edit-study/:studyId",
                element: <EditStudy />,
                id: RouteId.MarketResearch.EditStudy,
              },
            ],
          },
          {
            path: "/deepchat",
            element: <DeepchatPage />,
            id: RouteId.Deepchat.Root,
            children: [
              {
                path: "/deepchat",
                element: <AskQuestion />,
                id: RouteId.Deepchat.AskQuestion,
              },
              {
                path: "/deepchat/threads/:id?",
                element: <SessionThread />,
                id: RouteId.Deepchat.SessionThread,
              },
              {
                path: "/deepchat/summarize",
                element: <SummarizeDocument />,
                id: RouteId.Deepchat.SummarizeDocument,
              },
              {
                path: "/deepchat/sales-data",
                element: <QuerSalesData />,
                id: RouteId.Deepchat.QuerySales,
              },
            ],
          },
          {
            path: "/salesdata",
            element: <SalesDataPage />,
            id: RouteId.SalesData.Root,
            children: [
              {
                path: "/salesdata/:question?",
                element: <SalesDataAsk />,
                id: RouteId.SalesData.AskQuestion,
              },
              {
                path: "/salesdata/session",
                element: <SalesDataSession />,
                id: RouteId.SalesData.Session,
              },
            ],
          },
          {
            path: "/dashboard/",
            element: <CustomDashboardLanding />,
            id: RouteId.Dashboards.Landing,
          },
          {
            path: "/dashboards/retros",
            element: <RetrosPage />,
            id: RouteId.Retros.Root,
          },
          {
            // Could make sense as a child to dashboards but I think
            // medinfo is going away
            path: "/dashboards/custom/:id",
            element: <CustomDashboard />,
            id: RouteId.Dashboards.Root,
            children: [
              {
                path: "edit",
                element: <CustomDashboard />,
              },
            ],
          },
          {
            path: "/talktodata",
            element: <TalkToDataPage />,
            id: RouteId.TalkToData.Root,
          },
        ],
      },
    ],
  },
];
